import { ReactElement } from 'react'

import { useAtomValue } from 'jotai'
import { useNavigate } from 'react-router-dom'

import {
  SequenceMenu,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { formatISODate } from '@sequencehq/utils'

import { useCreateAggregatedLineItemGroups } from 'CreditNotes/hooks/useCreateAggregatedLineItemGroups'
import {
  invoiceBillingPeriodAtom,
  invoiceCurrencyAtom,
  invoiceCustomerIdAtom,
  invoiceCustomerTaxIdAtom,
  invoiceIdAtom,
  isSavePendingAtom,
  lineItemGroupsAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { rollUpGroupsIntoLineItems } from './rollUpGroupsIntoLineItems'
import { usePermissions } from 'lib/rbac'
import { getCreditNoteBillingPeriod } from 'CreditNotes/utils/getCreditNoteBillingPeriod.ts'

export const MenuItemCreateCreditNote = () => {
  const isSavePending = useAtomValue(isSavePendingAtom)

  const { can } = usePermissions()

  if (!can('org.creditNote:create')) {
    return null
  }

  return (
    <DialogCreateCreditNote
      trigger={
        <SequenceMenu.Item
          uuid="create-credit-note"
          label="Create credit note"
          isDisabled={isSavePending}
          data-testid="header.kebab-menu.create-credit-note"
        />
      }
    />
  )
}

const DialogCreateCreditNote = ({ trigger }: { trigger: ReactElement }) => {
  const { displayNotification } = useNotifications()
  const navigate = useNavigate()
  const createAggregatedCreditNoteLineItemGroups =
    useCreateAggregatedLineItemGroups()

  const invoiceId = useAtomValue(invoiceIdAtom)
  const currency = useAtomValue(invoiceCurrencyAtom)
  const customerId = useAtomValue(invoiceCustomerIdAtom)
  const customerTaxId = useAtomValue(invoiceCustomerTaxIdAtom)
  const billingPeriod = useAtomValue(invoiceBillingPeriodAtom)
  const lineItemGroups = useAtomValue(lineItemGroupsAtom)

  const postCreditNoteMutation = useMutation(
    dashboardv99990101Client.postCreditNote,
    {
      onError: () => {
        displayNotification('Failed to create credit note', {
          type: 'error'
        })
      }
    }
  )

  const { billingPeriodStart, billingPeriodEnd } =
    getCreditNoteBillingPeriod(billingPeriod)

  return (
    <SimpleModal
      trigger={trigger}
      variant="dialog"
      title="Create credit note"
      submitButtonText="Create credit note"
      submittingButtonText="Creating..."
      data-testid="modal.create-credit-note"
      onSubmit={async () => {
        if (!invoiceId || !currency || !customerId || !lineItemGroups) {
          return false
        }

        const res = await postCreditNoteMutation.mutateAsync({
          body: {
            currency,
            customerId,
            invoiceId,
            accountingDate: formatISODate(new Date()),
            billingPeriodStart,
            billingPeriodEnd,
            customerTaxId
          }
        })

        if (!res) return false

        try {
          const aggregatedGroups = rollUpGroupsIntoLineItems(
            lineItemGroups.map(group => {
              const { lineItems, ...rest } = group
              return rest
            }),
            lineItemGroups.flatMap(({ lineItems }) => lineItems),
            {
              id: 'client-group-id',
              title: 'Credit note'
            }
          )

          await createAggregatedCreditNoteLineItemGroups(
            res.id,
            !aggregatedGroups.length ? emptyLineItemGroup() : aggregatedGroups
          )
        } catch {
          displayNotification('Failed to create credit note', {
            type: 'error'
          })

          return false
        }

        navigate(`/credit-notes/${res.id}`)
        displayNotification('Credit note created', { type: 'success' })

        return true
      }}
    >
      To correct the original invoice you can issue a credit note and maintain
      an audit trail.
    </SimpleModal>
  )
}

function emptyLineItemGroup() {
  return [
    {
      title: 'Credit note',
      lineItems: []
    }
  ]
}
